<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleClose"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
        <!-- 标题 -->
        <div class="mb-4">
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            批量更改分类
          </h3>
          <p class="text-sm text-gray-500">
            将选中的 {{ selectedCount }} 个敏感词移动到指定分类
          </p>
        </div>

        <!-- 表单内容 -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              目标分类 <span class="text-red-500">*</span>
            </label>
            <select
              v-model="selectedCategoryId"
              required
              class="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
              :disabled="loading"
            >
              <option value="">请选择目标分类</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.name }} - {{ getSeverityLabel(category.severity_level) }}
              </option>
            </select>
            <p class="mt-1 text-xs text-gray-500">
              选择要移动到的目标分类
            </p>
          </div>

          <!-- 预览 -->
          <div v-if="selectedCategory" class="bg-gray-50 rounded-lg p-3">
            <h4 class="text-sm font-medium text-gray-900 mb-2">将移动到：</h4>
            <div class="flex items-center space-x-2">
              <span 
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="getSeverityBadgeClass(selectedCategory.severity_level)"
              >
                {{ selectedCategory.name }}
              </span>
              <span class="text-sm text-gray-600">
                {{ getSeverityLabel(selectedCategory.severity_level) }}
              </span>
            </div>
            <p v-if="selectedCategory.description" class="mt-2 text-xs text-gray-500">
              {{ selectedCategory.description }}
            </p>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="flex items-center justify-end space-x-3 mt-6">
          <button
            @click="handleClose"
            :disabled="loading"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            取消
          </button>
          <button
            @click="handleConfirm"
            :disabled="!selectedCategoryId || loading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
          >
            <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ loading ? '处理中...' : '确认更改' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  categories: {
    type: Array,
    default: () => []
  },
  selectedCount: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close', 'confirm'])

const loading = ref(false)
const selectedCategoryId = ref('')

const selectedCategory = computed(() => {
  return props.categories.find(cat => cat.id == selectedCategoryId.value)
})

const getSeverityLabel = (severity) => {
  const labels = {
    'low': '低风险',
    'medium': '中风险',
    'high': '高风险',
    'forbidden': '禁止使用'
  }
  return labels[severity] || severity
}

const getSeverityBadgeClass = (severity) => {
  const classes = {
    'low': 'bg-blue-100 text-blue-800',
    'medium': 'bg-yellow-100 text-yellow-800',
    'high': 'bg-orange-100 text-orange-800',
    'forbidden': 'bg-red-100 text-red-800'
  }
  return classes[severity] || 'bg-gray-100 text-gray-800'
}

const handleClose = () => {
  if (!loading.value) {
    selectedCategoryId.value = ''
    emit('close')
  }
}

const handleConfirm = () => {
  if (selectedCategoryId.value && !loading.value) {
    emit('confirm', selectedCategoryId.value)
    selectedCategoryId.value = ''
  }
}

// 监听弹窗显示/隐藏
watch(() => props.show, (newValue) => {
  if (newValue) {
    selectedCategoryId.value = ''
  }
})
</script>
